import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle, Camera } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicBooking() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [sessionType, setSessionType] = useState('');
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/public/book`, {
        name,
        email,
        preferred_date: preferredDate,
        preferred_time: preferredTime || undefined,
        session_type: sessionType || undefined,
        message: message || undefined,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 border border-success/20 rounded-full mb-4">
            <CheckCircle className="w-8 h-8 text-success" />
          </div>
          <h1 className="text-3xl font-serif font-bold text-foreground mb-2" data-testid="booking-success-title">
            Booking Received!
          </h1>
          <p className="text-text-secondary mb-8">
            Thank you for your booking request. We&apos;ll be in touch soon to confirm your session.
          </p>
          <div className="bg-surface border border-border rounded-xl p-4 text-left">
            <div className="text-sm text-text-secondary mb-1">Your Details</div>
            <div className="text-foreground font-medium">{name}</div>
            <div className="text-sm text-text-secondary">{email}</div>
            {sessionType && (
              <div className="text-sm text-text-muted mt-1">Session: {sessionType}</div>
            )}
            <div className="text-sm text-text-muted mt-2">
              {preferredDate} {preferredTime && `at ${preferredTime}`}
            </div>
            {message && (
              <div className="text-sm text-text-muted mt-2 pt-2 border-t border-border">
                Message: {message}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4">
            <Camera className="w-8 h-8 text-background" />
          </div>
          <h1 className="text-3xl font-serif font-bold text-foreground mb-2" data-testid="booking-title">
            Book a Session
          </h1>
          <p className="text-text-secondary">Fill in your details and preferred date</p>
        </div>

        <div className="bg-surface border border-border rounded-xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Your Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                placeholder="Your name"
                data-testid="booking-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Email *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                placeholder="you@example.com"
                data-testid="booking-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Preferred Date *
              </label>
              <input
                type="date"
                value={preferredDate}
                onChange={(e) => setPreferredDate(e.target.value)}
                required
                className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                data-testid="booking-date-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Preferred Time
              </label>
              <input
                type="time"
                value={preferredTime}
                onChange={(e) => setPreferredTime(e.target.value)}
                className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                data-testid="booking-time-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Session Type
              </label>
              <select
                value={sessionType}
                onChange={(e) => setSessionType(e.target.value)}
                className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                data-testid="booking-session-type-select"
              >
                <option value="">Select session type...</option>
                <option value="Portrait">Portrait</option>
                <option value="Wedding">Wedding</option>
                <option value="Event">Event</option>
                <option value="Commercial">Commercial</option>
                <option value="Family">Family</option>
                <option value="Headshots">Headshots</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all resize-none"
                placeholder="Tell us about your photography needs..."
                data-testid="booking-message-input"
              />
            </div>

            {error && (
              <div className="text-alert text-sm bg-alert/10 border border-alert/20 rounded-lg p-3" data-testid="booking-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-background hover:bg-primary/90 font-medium rounded-lg px-4 py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="booking-submit-button"
            >
              {loading ? 'Submitting...' : 'Submit Booking Request'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
